// src/pages/CheckOut.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import { PageHeader, RowActions, SectionCard, StatusTag, fmtDateTime, tablePagination, useFlatPage } from "../components/ui";
import "./CheckPages.css";
import CheckOutInvoice from "../components/CheckOutInvoice";

import {
  Card,
  Row,
  Col,
  Space,
  Typography,
  Input,
  Button,
  Descriptions,
  Tag,
  Statistic,
  Table,
  InputNumber,
  Alert,
  Modal,
  Tooltip,
  Empty,
  Divider,
  Segmented,
} from "antd";

import {
  DollarOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { IconDoc, IconInfo, IconRefresh, IconSearch } from "../components/ui/icons";
import notify from "../utils/notify";

const { Text, Title } = Typography;

/* ---------- helpers ---------- */
const asNum = (v) => Number(v ?? 0);

const fmtCurrency = (ccy, n) => {
  const val = asNum(n);
  if (ccy && /^[A-Z]{3}$/.test(ccy)) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: ccy,
        maximumFractionDigits: 2,
      }).format(val);
    } catch {
      // ignore
    }
  }
  return `${ccy ? `${ccy} ` : ""}${val.toFixed(2)}`;
};

/* ---------- BookingStatus mapping ---------- */
const BOOKING_STATUS = {
  0: "Pending",
  1: "Confirmed",
  2: "CheckedIn",
  3: "Cancelled",
  4: "CheckedOut",
};

const NAME_TO_CODE = {
  pending: 0,
  confirmed: 1,
  checkedin: 2,
  cancelled: 3,
  checkedout: 4,
};

const pick = (o, keys) => keys.find((k) => o && Object.prototype.hasOwnProperty.call(o, k)) ?? null;
const read = (o, keys) => (o && keys.some((k) => k in o) ? o[pick(o, keys)] : undefined);

/** Robustly parse status from number, numeric string, or name string */
const getStatusValue = (b) => {
  const raw = read(b, [
    "status",
    "Status",
    "bookingStatus",
    "BookingStatus",
    "statusId",
    "StatusId",
    "statusCode",
    "StatusCode",
  ]);

  if (raw === undefined || raw === null) return undefined;

  if (typeof raw === "number") return raw;

  if (typeof raw === "string") {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;

    const key = raw.replace(/\s+/g, "").toLowerCase();
    if (NAME_TO_CODE[key] !== undefined) return NAME_TO_CODE[key];
  }

  return undefined;
};

const norm = (s) =>
  String(s ?? "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");

/* ============================================================
   CheckOutPage (Ant Design) — Check-Out ON TOP, Current Check-Outs BELOW
============================================================ */
const CheckOutPage = () => {
  useFlatPage();
  const { authData } = useAuth();
  const hotelId = authData?.hotelId;

  /* search / guest */
  const [searchTerm, setSearchTerm] = useState("");

  // typed message so “not found” is NOT treated as error
  const [statusMessage, setStatusMessage] = useState({ type: "", text: "" });
  const setMsg = (type, text) => setStatusMessage({ type, text });
  const clearMsg = () => setStatusMessage({ type: "", text: "" });

  const [isLoadingGuest, setIsLoadingGuest] = useState(false);

  const [guestDetails, setGuestDetails] = useState(null);
  const [hotelDetails, setHotelDetails] = useState({
    hotelName: "",
    address: "",
    phoneNumber: "",
  });

  /* additional charges */
  const [guestBillings, setGuestBillings] = useState([]);
  const [paymentUpdates, setPaymentUpdates] = useState({});
  const [masterPayment, setMasterPayment] = useState(null);

  /* accommodation payment */
  const [accomPay, setAccomPay] = useState(null);
  const [applyingAccomPay, setApplyingAccomPay] = useState(false);

  /* unified payoff */
  const [totalPay, setTotalPay] = useState(null);
  const [settlingAll, setSettlingAll] = useState(false);
  const [settlingPartial, setSettlingPartial] = useState(false);

  /* check-outs list */
  const [checkouts, setCheckouts] = useState([]);
  const [isCheckoutsLoading, setIsCheckoutsLoading] = useState(false);
  const [checkoutsError, setCheckoutsError] = useState("");

  // Current checkouts UX
  const [checkoutsQuery, setCheckoutsQuery] = useState("");
  const [checkoutsFilter, setCheckoutsFilter] = useState("All"); // All | Today | Last 7 days
  const [lastCheckoutBookingId, setLastCheckoutBookingId] = useState(null);
  const checkoutsCardRef = useRef(null);

  /* invoice */
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceDetails, setInvoiceDetails] = useState(null);

  /* info */
  const [showInfo, setShowInfo] = useState(false);

  const classifySearchIssue = (err) => {
    const httpStatus = err?.response?.status;
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.Message ||
      err?.message ||
      "Unable to search guest right now.";

    const m = String(msg).toLowerCase();

    // Normal outcomes (not system errors)
    if (httpStatus === 404 || m.includes("not found") || m.includes("active check-in") || m.includes("checked-in")) {
      return { type: "info", text: "No active checked-in booking found for that guest / room." };
    }

    if (httpStatus === 400) return { type: "warning", text: msg };

    return { type: "error", text: msg };
  };

  /* -------------- fetch hotel header -------------- */
  useEffect(() => {
    if (!hotelId) return;
    (async () => {
      try {
        const res = await axiosInstance.get(`/Hotel/${hotelId}`);
        setHotelDetails({
          hotelName: res.data?.hotelName || "Hotel Name",
          address: res.data?.address || "Hotel Address",
          phoneNumber: res.data?.contactNumber || "+000 000 0000",
        });
      } catch {
        // ignore
      }
    })();
  }, [hotelId]);

  const getPaid = (r) => asNum(r?.PaidAmount ?? r?.paidAmount ?? r?.paid ?? 0);

  /* -------------- fetch guest billings -------------- */
  const fetchGuestBillings = async (guestId, bookingId) => {
    if (!guestId || !bookingId || !hotelId) return;
    try {
      const res = await axiosInstance.get(`/GuestBillings/guest/${guestId}`, {
        params: { hotelId, bookingId },
      });
      setGuestBillings(Array.isArray(res.data) ? res.data : []);
      setPaymentUpdates({});
      setMasterPayment(null);
    } catch (err) {
      console.error("Error fetching guest billings:", err);
    }
  };

  /* -------------- search guest -------------- */
  const handleSearchGuest = async () => {
    if (!searchTerm.trim()) {
      setMsg("warning", "Please enter a valid search term (ID Number or Room Number).");
      return;
    }
    if (!hotelId) {
      setMsg("error", "Hotel ID is missing. Please log in again.");
      return;
    }

    setIsLoadingGuest(true);
    clearMsg();
    setGuestDetails(null);
    setGuestBillings([]);
    setPaymentUpdates({});
    setMasterPayment(null);
    setAccomPay(null);
    setTotalPay(null);

    try {
      const idOrRoom = searchTerm.trim();

      // Use allSettled so one 404 doesn't kill the other call
      const [guestResSettled, bookingsResSettled] = await Promise.allSettled([
        axiosInstance.get("/Guests/SearchByIdNumberAndHotel", {
          params: { idNumber: idOrRoom, hotelId },
        }),
        axiosInstance.get("/Bookings/SearchGuestCheckins", {
          params: { searchTerm: idOrRoom, hotelId },
        }),
      ]);

      const guest = guestResSettled.status === "fulfilled" ? guestResSettled.value?.data : null;
      const guestBookings =
        bookingsResSettled.status === "fulfilled"
          ? Array.isArray(bookingsResSettled.value?.data)
            ? bookingsResSettled.value.data
            : []
          : [];

      // If both are empty -> normal outcome
      if (!guest && guestBookings.length === 0) {
        setMsg("info", "No guest / active checked-in booking found for that search.");
        return;
      }

      // ✅ strictly select a CheckedIn booking (2)
      const checkedIn = guestBookings.find((b) => getStatusValue(b) === 2);
      if (!checkedIn) {
        setMsg("info", "Guest found, but there is no active check-in (CheckedIn booking) right now.");
        return;
      }

      const activeBooking = checkedIn;

      const totalAmount = asNum(activeBooking.totalAmount ?? activeBooking.TotalAmount);
      const paidAmount = asNum(activeBooking.paidAmount ?? activeBooking.PaidAmount);
      const pendingAmount = Math.max(totalAmount - paidAmount, 0);
      const currency = activeBooking.currency ?? activeBooking.Currency ?? "BWP";

      // Guest fallback: allow Room Number searches when guest endpoint returns 404
      const firstName =
        guest?.firstName ?? guest?.FirstName ?? activeBooking.firstName ?? activeBooking.FirstName ?? "";
      const lastName = guest?.lastName ?? guest?.LastName ?? activeBooking.lastName ?? activeBooking.LastName ?? "";

      const mappedGuestId =
        guest?.GuestId ??
        guest?.guestId ??
        activeBooking.guestId ??
        activeBooking.GuestId ??
        activeBooking.customerId ??
        activeBooking.CustomerId ??
        null;

      const mappedBookingId = activeBooking.bookingId ?? activeBooking.BookingId ?? 0;

      const idNumber =
        guest?.idNumber ?? guest?.IdNumber ?? activeBooking.idNumber ?? activeBooking.IdNumber ?? "";

      setGuestDetails({
        name: `${firstName} ${lastName}`.trim(),
        firstName,
        lastName,
        email: guest?.email ?? guest?.Email ?? activeBooking.email ?? activeBooking.Email ?? "N/A",
        mobile:
          guest?.mobileNumber ??
          guest?.MobileNumber ??
          guest?.phoneNumber ??
          guest?.PhoneNumber ??
          activeBooking.mobileNumber ??
          activeBooking.MobileNumber ??
          "N/A",
        idNumber,
        roomNumber: activeBooking.roomNumber ?? activeBooking.RoomNumber ?? "N/A",
        roomId: activeBooking.roomId ?? activeBooking.RoomId ?? 0,
        bookingNo: activeBooking.bookingNo ?? activeBooking.BookingNo ?? "N/A",
        bookingId: mappedBookingId,
        totalAmount,
        paidAmount,
        pendingAmount,
        guestId: mappedGuestId,
        hotelId,
        currency,
        nights: activeBooking.daysBooked ?? activeBooking.DaysBooked ?? 1,
        checkInDate: activeBooking.checkInDate ?? activeBooking.CheckInDate,
        checkOutDate: activeBooking.checkOutDate ?? activeBooking.CheckOutDate,
        bookingStatus: 2, // enforce CheckedIn
      });

      setMsg("success", "Guest details loaded successfully.");

      if (!mappedGuestId) {
        setMsg("warning", "Booking found, but guestId is missing in the payload. Unable to load additional charges.");
        return;
      }

      await fetchGuestBillings(mappedGuestId, mappedBookingId);
    } catch (error) {
      console.error("Search guest failed:", error);
      const classified = classifySearchIssue(error);
      setMsg(classified.type, classified.text);
    } finally {
      setIsLoadingGuest(false);
    }
  };

  /* -------------- checkouts list -------------- */
  const fetchCheckouts = async () => {
    if (!hotelId) return;
    setIsCheckoutsLoading(true);
    setCheckoutsError("");

    try {
      const res = await axiosInstance.get(`/hotels/${hotelId}/Status/checkedout`);
      setCheckouts(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setCheckoutsError(error?.response?.data?.message || error?.response?.data?.Message || "Failed to fetch check-outs.");
    } finally {
      setIsCheckoutsLoading(false);
    }
  };

  useEffect(() => {
    fetchCheckouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId]);

  /* -------------- totals -------------- */
  const totalAdditionalOwed = useMemo(() => {
    return guestBillings.reduce((sum, b) => {
      const owed = Math.max(asNum(b.amount ?? b.Amount) - getPaid(b), 0);
      return sum + owed;
    }, 0);
  }, [guestBillings]);

  const outstandingTotal = useMemo(() => {
    const accom = asNum(guestDetails?.pendingAmount || 0);
    return accom + totalAdditionalOwed;
  }, [guestDetails, totalAdditionalOwed]);

  // ✅ only allow when folio clear AND booking is CheckedIn
  const canCheckOut = outstandingTotal <= 0.001 && !!guestDetails && guestDetails.bookingStatus === 2;

  /* -------------- additional payments -------------- */
  const setPaymentFor = (billingId, value) => {
    setPaymentUpdates((prev) => ({ ...prev, [billingId]: asNum(value) }));
  };

  const applyMasterPayment = () => {
    const masterVal = asNum(masterPayment);
    if (masterVal <= 0) {
      setMsg("warning", "Please enter a valid master payment amount.");
      return;
    }
    if (Math.abs(masterVal - totalAdditionalOwed) > 0.01) {
      setMsg(
        "warning",
        `Master payment must equal total outstanding additional charges (${fmtCurrency(
          guestDetails?.currency,
          totalAdditionalOwed
        )}).`
      );
      return;
    }

    const updates = {};
    guestBillings.forEach((b) => {
      const owed = Math.max(asNum(b.amount ?? b.Amount) - getPaid(b), 0);
      if (owed > 0) updates[b.billingId ?? b.BillingId] = owed;
    });

    setPaymentUpdates(updates);
    setMsg("success", "Master payment applied to all billing records.");
  };

  const submitAdditionalPaymentsMap = async (updatesMap) => {
    if (!guestDetails) return false;

    let didAny = false;

    for (const b of guestBillings) {
      const id = b.billingId ?? b.BillingId;
      const owed = Math.max(asNum(b.amount ?? b.Amount) - getPaid(b), 0);
      const add = Math.min(owed, asNum(updatesMap[id]));

      if (add > 0) {
        didAny = true;
        const newPaid = getPaid(b) + add;

        await axiosInstance.put(`/GuestBillings/${id}`, {
          GuestId: b.GuestId ?? b.guestId ?? guestDetails.guestId,
          HotelId: hotelId,
          RoomNumber: b.RoomNumber ?? b.roomNumber ?? guestDetails.roomNumber,
          BookingNo: b.bookingNo ?? b.BookingNo ?? guestDetails.bookingNo,
          BookingId: guestDetails.bookingId,
          Description: b.description ?? b.Description,
          Amount: b.amount ?? b.Amount,
          PaidAmount: newPaid,
          BilledBy: b.billedBy ?? b.BilledBy,
        });
      }
    }

    if (didAny) {
      await fetchGuestBillings(guestDetails.guestId, guestDetails.bookingId);
    }

    return didAny;
  };

  const submitAdditionalPayments = async () => {
    try {
      const didAny = await submitAdditionalPaymentsMap(paymentUpdates);
      if (didAny) {
        notify.success({ message: "Additional payments applied" });
        setPaymentUpdates({});
      } else {
        notify.info({ message: "No additional payments entered" });
      }
    } catch (e) {
      console.error(e);
      notify.error({
        message: "Failed to apply additional payments",
        description: e?.response?.data?.message || e?.response?.data?.Message || "Please try again.",
      });
    }
  };

  /* -------------- accommodation payment -------------- */
  const applyAccommodationPaymentAmount = async (amount) => {
    if (!guestDetails) return false;

    const add = asNum(amount);
    if (add <= 0) return false;

    const currentPaid = asNum(guestDetails.paidAmount);
    const total = asNum(guestDetails.totalAmount);

    const newPaid = Math.min(currentPaid + add, total);
    if (newPaid === currentPaid) return false;

    // ✅ IMPORTANT: Use /paid-amount endpoint
    const res = await axiosInstance.put(`/Bookings/${guestDetails.bookingId}/paid-amount`, {
      paidAmount: newPaid,
    });

    // If API returns booking summary, use it; otherwise recalc locally
    const apiPaid = res?.data?.paidAmount ?? res?.data?.PaidAmount;
    const apiTotal = res?.data?.totalAmount ?? res?.data?.TotalAmount;

    const finalPaid = apiPaid != null ? asNum(apiPaid) : newPaid;
    const finalTotal = apiTotal != null ? asNum(apiTotal) : total;

    const pending = Math.max(finalTotal - finalPaid, 0);

    setGuestDetails((g) =>
      g
        ? {
            ...g,
            paidAmount: finalPaid,
            totalAmount: finalTotal,
            pendingAmount: pending,
          }
        : g
    );

    return true;
  };

  const applyAccommodationPayment = async () => {
    try {
      setApplyingAccomPay(true);
      const ok = await applyAccommodationPaymentAmount(accomPay);

      if (ok) {
        setAccomPay(null);
        notify.success({ message: "Accommodation payment applied" });
      } else {
        notify.info({ message: "No accommodation payment applied" });
      }
    } catch (e) {
      console.error(e);
      notify.error({
        message: "Failed to apply accommodation payment",
        description: e?.response?.data?.message || e?.response?.data?.Message || "Please try again.",
      });
    } finally {
      setApplyingAccomPay(false);
    }
  };

  /* -------------- unified payoff -------------- */
  const handleSettleAll = async () => {
    if (!guestDetails) return;

    if (outstandingTotal <= 0) {
      notify.info({ message: "Nothing to settle" });
      return;
    }

    setSettlingAll(true);

    try {
      const accomPending = asNum(guestDetails.pendingAmount);
      if (accomPending > 0) {
        await applyAccommodationPaymentAmount(accomPending);
      }

      if (totalAdditionalOwed > 0) {
        const updates = {};
        guestBillings.forEach((b) => {
          const owed = Math.max(asNum(b.amount ?? b.Amount) - getPaid(b), 0);
          if (owed > 0) updates[b.billingId ?? b.BillingId] = owed;
        });
        await submitAdditionalPaymentsMap(updates);
      }

      setTotalPay(null);
      notify.success({ message: "All balances settled" });
    } catch (e) {
      console.error(e);
      notify.error({
        message: "Failed to settle all",
        description: e?.response?.data?.message || e?.response?.data?.Message || "Please try again.",
      });
    } finally {
      setSettlingAll(false);
    }
  };

  const handleSettlePartial = async () => {
    if (!guestDetails) return;

    const amount = asNum(totalPay);
    if (amount <= 0) {
      notify.info({ message: "Enter a valid amount to pay" });
      return;
    }

    setSettlingPartial(true);

    try {
      let remaining = amount;

      const accomPending = asNum(guestDetails.pendingAmount);
      if (accomPending > 0) {
        const payAccom = Math.min(remaining, accomPending);
        if (payAccom > 0) {
          await applyAccommodationPaymentAmount(payAccom);
          remaining -= payAccom;
        }
      }

      if (remaining > 0 && totalAdditionalOwed > 0) {
        const updates = {};
        for (const b of guestBillings) {
          const id = b.billingId ?? b.BillingId;
          const owed = Math.max(asNum(b.amount ?? b.Amount) - getPaid(b), 0);
          if (owed <= 0) continue;

          const pay = Math.min(remaining, owed);
          if (pay > 0) {
            updates[id] = pay;
            remaining -= pay;
            if (remaining <= 0) break;
          }
        }

        if (Object.keys(updates).length > 0) {
          await submitAdditionalPaymentsMap(updates);
        }
      }

      setTotalPay(null);
      notify.success({ message: "Payment applied" });
    } catch (e) {
      console.error(e);
      notify.error({
        message: "Failed to apply payment",
        description: e?.response?.data?.message || e?.response?.data?.Message || "Please try again.",
      });
    } finally {
      setSettlingPartial(false);
    }
  };

  /* -------------- check-out -------------- */
  const handleCheckOut = async () => {
    if (!guestDetails) return;

    if (!canCheckOut) {
      notify.warning({
        message: "Settle all balances first",
        description: "Accommodation and additional charges must be fully paid before check-out.",
      });
      return;
    }

    try {
      const housekeepingTask = {
        roomId: guestDetails.roomId,
        userId: String(authData?.userId ?? authData?.username ?? "system"),
        status: "Pending",
        notes: "Room ready for cleaning.",
        hotelId: guestDetails.hotelId,
        assignedAt: new Date().toISOString(),
        taskType: "RoomCleaning",
        dueDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      };

      const checkOut = {
        bookingId: guestDetails.bookingId,
        checkoutDate: new Date().toISOString(),
        remarks: "Checked out successfully.",
      };

      await axiosInstance.post("/CheckOut/CheckOut", {
        checkOut,
        housekeepingTask,
      });

      notify.success({ message: "Guest checked out successfully" });

      // highlight the booking in Current Check-Outs
      setLastCheckoutBookingId(guestDetails.bookingId);

      setGuestDetails(null);
      setGuestBillings([]);
      setPaymentUpdates({});
      setMasterPayment(null);
      setAccomPay(null);
      setTotalPay(null);

      await fetchCheckouts();

      // optional: scroll to bottom card so user can immediately see the record
      setTimeout(() => {
        if (checkoutsCardRef.current) {
          checkoutsCardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 0);
    } catch (e) {
      console.error(e);
      notify.error({
        message: "Check-out failed",
        description: e?.response?.data?.message || e?.response?.data?.Message || "Please try again.",
      });
    }
  };

  /* -------------- invoice -------------- */
  const fetchInvoiceData = async (bookingId) => {
    try {
      const res = await axiosInstance.get(`/CheckOut/${bookingId}/invoice`);
      return res.data;
    } catch (e) {
      console.error("Error fetching invoice:", e);
      return null;
    }
  };

  const handleViewInvoice = async (bookingId) => {
    const data = await fetchInvoiceData(bookingId);
    if (!data) {
      notify.warning({ message: "No invoice data found." });
      return;
    }

    const inv = data.invoice ?? data.Invoice ?? {};
    const billingRecords = data.billingRecords ?? data.BillingRecords ?? [];

    const mapped = {
      guestDetails: {
        name: inv.guestName ?? inv.GuestName ?? "N/A",
        bookingNo: inv.bookingNo ?? inv.BookingNo ?? "N/A",
        roomNumber: inv.roomNumber ?? inv.RoomNumber ?? "N/A",
        paymentAmount: inv.amountPaid ?? inv.AmountPaid ?? 0,
        currency: inv.currency ?? inv.Currency ?? guestDetails?.currency ?? "BWP",
        basePrice: inv.basePrice ?? inv.BasePrice ?? 0,
        vatPercentage: inv.vatPercentage ?? inv.VatPercentage ?? 0,
        additionalLevies: inv.additionalLevies ?? inv.AdditionalLevies ?? 0,
        nights: inv.daysBooked ?? inv.DaysBooked ?? 0,
      },
      billingRecords,
    };

    setInvoiceDetails(mapped);
    setShowInvoiceModal(true);
  };

  /* -------------- Current Check-Outs normalize + filter + sort -------------- */
  const normalizeCheckoutRow = (r) => {
    const bookingId = r.bookingId ?? r.BookingId ?? r.id ?? r.Id ?? null;

    const firstName = r.firstName ?? r.FirstName ?? "";
    const lastName = r.lastName ?? r.LastName ?? "";
    const guestName = `${firstName} ${lastName}`.trim() || (r.guestName ?? r.GuestName ?? "");

    return {
      bookingId,
      bookingNo: r.bookingNo ?? r.BookingNo ?? "",
      guestId: r.guestId ?? r.GuestId ?? null,
      idNumber: r.idNumber ?? r.IdNumber ?? "", // requires API to return it
      guestName,
      roomNumber: r.roomNumber ?? r.RoomNumber ?? "",
      checkOutDate: r.checkOutDate ?? r.CheckOutDate ?? null,
      totalAmount: r.totalAmount ?? r.TotalAmount ?? 0,
      currency: r.currency ?? r.Currency ?? "BWP",
    };
  };

  const isSameLocalDate = (a, b) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const isWithinLastDays = (d, days) => {
    const now = new Date();
    const ms = now.getTime() - d.getTime();
    return ms >= 0 && ms <= days * 24 * 60 * 60 * 1000;
  };

  const visibleCheckouts = useMemo(() => {
    const rows = (Array.isArray(checkouts) ? checkouts : []).map(normalizeCheckoutRow);

    const q = norm(checkoutsQuery);
    const searched = rows.filter((x) => {
      if (!q) return true;

      return (
        norm(x.idNumber).includes(q) ||
        norm(x.guestName).includes(q) ||
        norm(x.roomNumber).includes(q) ||
        norm(x.bookingNo).includes(q)
      );
    });

    const dateFiltered = searched.filter((x) => {
      if (checkoutsFilter === "All") return true;
      const d = x.checkOutDate ? new Date(x.checkOutDate) : null;
      if (!d || Number.isNaN(d.getTime())) return false;

      if (checkoutsFilter === "Today") return isSameLocalDate(d, new Date());
      if (checkoutsFilter === "Last 7 days") return isWithinLastDays(d, 7);
      return true;
    });

    // newest first
    dateFiltered.sort((a, b) => {
      const at = a.checkOutDate ? new Date(a.checkOutDate).getTime() : 0;
      const bt = b.checkOutDate ? new Date(b.checkOutDate).getTime() : 0;
      return bt - at;
    });

    return dateFiltered;
  }, [checkouts, checkoutsQuery, checkoutsFilter]);

  /* -------------- columns (Guest Billing) -------------- */
  const billingColumns = [
    {
      title: "Billing ID",
      dataIndex: "billingId",
      width: 110,
      fixed: "left",
      render: (v, r) => v ?? r.BillingId,
    },
    {
      title: "Description",
      dataIndex: "description",
      ellipsis: true,
      render: (v, r) => v ?? r.Description,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      align: "right",
      render: (v, r) => fmtCurrency(guestDetails?.currency, v ?? r.Amount),
      width: 140,
    },
    {
      title: "Paid",
      dataIndex: "PaidAmount",
      align: "right",
      render: (_, r) => fmtCurrency(guestDetails?.currency, getPaid(r)),
      width: 140,
    },
    {
      title: "Owed",
      key: "owed",
      align: "right",
      render: (_, r) => fmtCurrency(guestDetails?.currency, Math.max(asNum(r.amount ?? r.Amount) - getPaid(r), 0)),
      width: 140,
    },
    {
      title: "Added",
      dataIndex: "createdDate",
      render: (v, r) => ((v ?? r.CreatedDate) ? new Date(v ?? r.CreatedDate).toLocaleString() : "-"),
      width: 180,
    },
    {
      title: "Pay Now",
      key: "paynow",
      width: 180,
      render: (_, r) => (
        <InputNumber
          min={0}
          step={0.01}
          style={{ width: "100%" }}
          placeholder="Enter amount"
          value={paymentUpdates[r.billingId ?? r.BillingId] ?? 0}
          onChange={(val) => setPaymentFor(r.billingId ?? r.BillingId, val)}
          addonBefore={<DollarOutlined />}
        />
      ),
    },
  ];

  return (
    <div className="hms-page co-page">
      <PageHeader
        actions={
          <Button icon={<IconInfo />} onClick={() => setShowInfo(true)}>
            Guide
          </Button>
        }
      />

      {/* Find a guest, then settle and check out */}
      <SectionCard title="Find a guest">
        <div className="co-search" style={{ marginBottom: 12 }}>
          <Input
            allowClear
            placeholder="Enter ID number or room number"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onPressEnter={handleSearchGuest}
            prefix={<IconSearch />}
            maxLength={20}
          />
          <Button type="primary" icon={<IconSearch />} onClick={handleSearchGuest} loading={isLoadingGuest}>
            Search
          </Button>
        </div>

        {!!statusMessage?.text && (
          <Alert style={{ marginBottom: 12 }} type={statusMessage.type || "info"} message={statusMessage.text} showIcon />
        )}

        {!guestDetails ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Search a guest to begin check-out" />
        ) : (
          <>
            <Descriptions size="small" column={3} title="Guest summary">
              <Descriptions.Item label="Name">
                {guestDetails.firstName} {guestDetails.lastName}
              </Descriptions.Item>
              <Descriptions.Item label="ID Number">{guestDetails.idNumber || "—"}</Descriptions.Item>
              <Descriptions.Item label="Room">{guestDetails.roomNumber}</Descriptions.Item>
              <Descriptions.Item label="Booking No">{guestDetails.bookingNo}</Descriptions.Item>
              <Descriptions.Item label="Nights">{guestDetails.nights}</Descriptions.Item>
              <Descriptions.Item label="Folio Status">
                <StatusTag status={outstandingTotal > 0 ? "pending" : "available"}>
                  {outstandingTotal > 0 ? "Outstanding" : "Clear"}
                </StatusTag>
              </Descriptions.Item>
              <Descriptions.Item label="Booking Status">
                <StatusTag status={BOOKING_STATUS[guestDetails.bookingStatus]}>
                  {BOOKING_STATUS[guestDetails.bookingStatus] ?? "Unknown"}
                </StatusTag>
              </Descriptions.Item>
            </Descriptions>

            <Row gutter={16} style={{ marginTop: 12 }}>
              <Col xs={24} md={8}>
                <Card size="small">
                  <Statistic title="Accommodation Total" value={fmtCurrency(guestDetails.currency, guestDetails.totalAmount)} />
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card size="small">
                  <Statistic title="Accommodation Paid" value={fmtCurrency(guestDetails.currency, guestDetails.paidAmount)} />
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card size="small">
                  <Statistic
                    title="Accommodation Pending"
                    value={fmtCurrency(guestDetails.currency, guestDetails.pendingAmount)}
                    valueStyle={{ color: guestDetails.pendingAmount > 0 ? "#fa541c" : "#52c41a" }}
                  />
                </Card>
              </Col>
            </Row>

            <Divider />

            <Row gutter={16}>
              {/* Left: Accommodation */}
              <Col xs={24} md={10}>
                <Card
                  size="small"
                  title={
                    <Space>
                      <DollarOutlined />
                      <Text strong>Accommodation Payment</Text>
                    </Space>
                  }
                  extra={<Tag>Booking</Tag>}
                >
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <InputNumber
                      min={0}
                      step={0.01}
                      style={{ width: "100%" }}
                      placeholder="Enter amount"
                      value={accomPay}
                      onChange={setAccomPay}
                      addonBefore={<DollarOutlined />}
                    />
                    <Button
                      type="primary"
                      block
                      onClick={applyAccommodationPayment}
                      loading={applyingAccomPay}
                      disabled={guestDetails.pendingAmount <= 0}
                    >
                      Apply Accommodation Payment
                    </Button>
                    <Alert
                      type="info"
                      showIcon
                      message="You can pay accommodation and additional charges separately. Check-out is enabled once both are fully settled."
                    />
                  </Space>
                </Card>
              </Col>

              {/* Right: Additional Charges */}
              <Col xs={24} md={14}>
                <Card
                  size="small"
                  title={
                    <Space>
                      <IconInfo />
                      <Text strong>Additional Charges (Guest Billing)</Text>
                    </Space>
                  }
                  extra={<Tag>F&amp;B / Other</Tag>}
                >
                  {guestBillings.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No additional billing records" />
                  ) : (
                    <>
                      <Table
                        className="hms-table"
                        rowKey={(r) => r.billingId ?? r.BillingId}
                        columns={billingColumns}
                        dataSource={guestBillings}
                        pagination={tablePagination("charge", { pageSize: 5, showSizeChanger: false })}
                        scroll={{ x: true }}
                      />

                      <Row gutter={8} style={{ marginTop: 8 }}>
                        <Col xs={24} md={12}>
                          <Space.Compact style={{ width: "100%" }}>
                            <InputNumber
                              min={0}
                              step={0.01}
                              style={{ width: "100%" }}
                              placeholder="Master Payment (must equal total owed)"
                              value={masterPayment}
                              onChange={setMasterPayment}
                              addonBefore={<DollarOutlined />}
                            />
                            <Button onClick={applyMasterPayment}>Fill All</Button>
                          </Space.Compact>
                        </Col>
                        <Col xs={24} md={12} style={{ textAlign: "right" }}>
                          <Space direction="vertical" size={0} style={{ width: "100%" }}>
                            <Text type="secondary">Total Additional Owed</Text>
                            <Title level={4} style={{ margin: 0 }}>
                              {fmtCurrency(guestDetails.currency, totalAdditionalOwed)}
                            </Title>
                          </Space>
                        </Col>
                      </Row>

                      <Space style={{ marginTop: 8 }}>
                        <Button type="primary" onClick={submitAdditionalPayments}>
                          Apply Additional Payments
                        </Button>
                      </Space>
                    </>
                  )}
                </Card>
              </Col>
            </Row>

            <Divider />

            {/* Footer actions with UNIFIED PAYOFF */}
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Card size="small">
                  <Space direction="vertical" size={6} style={{ width: "100%" }}>
                    <Space style={{ justifyContent: "space-between", width: "100%" }}>
                      <Text type="secondary">Total Outstanding (All)</Text>
                      <Title level={3} style={{ margin: 0 }}>
                        {fmtCurrency(guestDetails.currency, outstandingTotal)}
                      </Title>
                    </Space>

                    <Space.Compact style={{ width: "100%" }}>
                      <InputNumber
                        min={0}
                        step={0.01}
                        style={{ width: "100%" }}
                        placeholder="Pay total outstanding (or any amount)"
                        value={totalPay}
                        onChange={setTotalPay}
                        addonBefore={<DollarOutlined />}
                      />
                      <Button onClick={handleSettlePartial} disabled={outstandingTotal <= 0} loading={settlingPartial}>
                        Pay Amount
                      </Button>
                      <Button type="primary" onClick={handleSettleAll} disabled={outstandingTotal <= 0} loading={settlingAll}>
                        Settle All
                      </Button>
                    </Space.Compact>
                  </Space>
                </Card>
              </Col>

              <Col xs={24} md={12} style={{ textAlign: "right" }}>
                <Space wrap>
                  <Button icon={<IconDoc />} onClick={() => handleViewInvoice(guestDetails.bookingId)}>
                    View Invoice
                  </Button>
                  <Tooltip title={canCheckOut ? "All settled. Proceed to check-out." : "Settle all balances to enable check-out."}>
                    <Button type="primary" icon={<CheckCircleOutlined />} onClick={handleCheckOut} disabled={!canCheckOut}>
                      Confirm Check-Out
                    </Button>
                  </Tooltip>
                </Space>
              </Col>
            </Row>
          </>
        )}
      </SectionCard>

      {/* ===========================
          Current Check-Outs (BOTTOM)
      ============================ */}
      <div ref={checkoutsCardRef}>
      <SectionCard
        flush
        title={
          <>
            Current check-outs
            <span className="hms-count">{visibleCheckouts.length}</span>
          </>
        }
        extra={
          <Space wrap>
            <Segmented value={checkoutsFilter} onChange={setCheckoutsFilter} options={["All", "Today", "Last 7 days"]} />
            <Input
              allowClear
              style={{ width: 280 }}
              placeholder="Search by ID Number / Name / Room / Booking No"
              value={checkoutsQuery}
              onChange={(e) => setCheckoutsQuery(e.target.value)}
              prefix={<IconSearch />}
            />
            <Button icon={<IconRefresh />} onClick={fetchCheckouts} loading={isCheckoutsLoading}>
              Refresh
            </Button>
          </Space>
        }
      >
        {checkoutsError && <Alert type="error" showIcon message={checkoutsError} style={{ margin: 16 }} />}

        {isCheckoutsLoading ? (
          <div style={{ padding: 24, textAlign: "center" }}>
            <Text type="secondary">Loading check-outs…</Text>
          </div>
        ) : visibleCheckouts.length > 0 ? (
          <Table
            className="hms-table"
            rowKey={(r) => String(r.bookingId ?? `${r.guestName}-${r.roomNumber}-${r.checkOutDate}`)}
            pagination={tablePagination("check-out")}
            rowClassName={(r) =>
              lastCheckoutBookingId && String(r.bookingId) === String(lastCheckoutBookingId) ? "row-highlight" : ""
            }
            columns={[
              {
                title: "Guest",
                dataIndex: "guestName",
                render: (v, r) => (
                  <div className="hms-cell-stack">
                    <span className="hms-cell-main">{v || "—"}</span>
                    <span className="hms-cell-sub">{r.idNumber || "No ID number"}</span>
                  </div>
                ),
              },
              { title: "Room", dataIndex: "roomNumber", width: 110, render: (v) => v || "—" },
              {
                title: "Check-Out",
                dataIndex: "checkOutDate",
                width: 200,
                render: (v) =>
                  fmtDateTime(v),
              },
              {
                title: "Total",
                dataIndex: "totalAmount",
                align: "right",
                width: 140,
                render: (v, r) => (v == null ? "-" : fmtCurrency(r.currency || "BWP", v)),
              },
              {
                title: "",
                width: 130,
                align: "right",
                render: (_, r) => (
                  <RowActions
                    actions={[
                      {
                        key: "invoice",
                        label: "Invoice",
                        icon: "document",
                        showLabel: true,
                        onClick: () => handleViewInvoice(r.bookingId),
                      },
                    ]}
                  />
                ),
              },
            ]}
            dataSource={visibleCheckouts}
          />
        ) : (
          <div className="co-empty">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No check-outs found" />
          </div>
        )}
      </SectionCard>
      </div>

      {/* Invoice Modal */}
      {showInvoiceModal && invoiceDetails && (
        <CheckOutInvoice
          isOpen={showInvoiceModal}
          onClose={() => setShowInvoiceModal(false)}
          guestDetails={invoiceDetails.guestDetails}
          hotelDetails={hotelDetails}
          billingRecords={invoiceDetails.billingRecords}
        />
      )}

      {/* Info Modal */}
      <Modal
        title="How check-out works"
        open={showInfo}
        onCancel={() => setShowInfo(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowInfo(false)}>
            Got it
          </Button>,
        ]}
        width={640}
        centered
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Alert type="info" showIcon message="Guests must settle both Accommodation and Additional charges to check out." />
          <ul style={{ paddingInlineStart: 18, margin: 0 }}>
            <li>
              <Text>
                Use <strong>Accommodation Payment</strong> to post room-night payments.
              </Text>
            </li>
            <li>
              <Text>
                Use <strong>Additional Charges</strong> to settle F&amp;B/other bills, per-row or via Master Payment.
              </Text>
            </li>
            <li>
              <Text>
                Or use the <strong>footer controls</strong> to pay a custom amount or <em>Settle All</em> in one go.
              </Text>
            </li>
          </ul>
          <Alert
            type="warning"
            showIcon
            message={
              <Space>
                <ExclamationCircleOutlined />
                Accommodation payment is mapped to <strong>PUT /Bookings/:id/paid-amount</strong> to avoid the room-price
                recalculation 404.
              </Space>
            }
          />
        </Space>
      </Modal>

      {/* tiny highlight style (optional, harmless if you already have CSS) */}
      <style>{`
        .row-highlight td { background: var(--hms-wash) !important; }
      `}</style>
    </div>
  );
};

export default CheckOutPage;
